import React, { useState, useEffect } from "react";
import { Form, Select, Typography, InputNumber, Input } from "antd";
import axios from "axios";
import { useTranslation } from "react-i18next";

import { elvis } from "../../Utils";

const { Option } = Select;
const { Title } = Typography;

// Mirrors the server enum it.polimi.lode.model.building.BlockPosition.
const BLOCK_POSITIONS = [
    { value: "INTERNAL", label: "Internal" },
    { value: "EXTERNAL", label: "External" },
];

const AddedBlockForm = props => {
    const { t } = useTranslation();
    const [form] = Form.useForm();
    const [materialTypes, setMaterialTypes] = useState([]);
    const [functionTypes, setFunctionTypes] = useState([]);
    const isEditMode = props.isEditMode || false;

    const formItemLayout = {
        labelCol: { sm: { span: 8 } },
        wrapperCol: { sm: { span: 16 } },
    };

    useEffect(() => {
        // Added blocks use the coarse material list.
        axios
            .get(process.env.REACT_APP_API_ENDPOINT + "/building/enums/non-ordinary-materials")
            .then(({ data }) => setMaterialTypes(data))
            .catch(error => console.log(error));
        axios
            .get(process.env.REACT_APP_API_ENDPOINT + "/building/enums/added-block-functions")
            .then(({ data }) => setFunctionTypes(data))
            .catch(error => console.log(error));
    }, []);

    const assetFields = (asset) => ({
        position: asset.position,
        verticalMat: asset.verticalMat,
        horizontalMat: asset.horizontalMat,
        nFloors: asset.nFloors,
        totalHeight: asset.totalHeight,
        floorSurface: asset.floorSurface,
        floorHeight: asset.floorHeight,
        function: asset.function,
        previousSurvey: asset.previousSurvey,
    });

    useEffect(() => {
        if (props.record.asset) {
            form.setFieldsValue(assetFields(props.record.asset));
        }
    }, [props.record.asset, form]);

    useEffect(() => {
        if (!isEditMode && props.record.asset) {
            form.resetFields();
            form.setFieldsValue(assetFields(props.record.asset));
        }
    }, [isEditMode, form, props.record.asset]);

    const handleFormChange = () => {
        if (!isEditMode) return;

        const values = form.getFieldsValue();
        const errors = form.getFieldsError();
        const isValid = errors.every(error => !error.errors.length);

        const newAsset = { ...props.record };
        newAsset.asset.position = elvis(values.position);
        newAsset.asset.verticalMat = elvis(values.verticalMat);
        newAsset.asset.horizontalMat = elvis(values.horizontalMat);
        newAsset.asset.nFloors = elvis(values.nFloors);
        newAsset.asset.totalHeight = elvis(values.totalHeight);
        newAsset.asset.floorSurface = elvis(values.floorSurface);
        newAsset.asset.floorHeight = elvis(values.floorHeight);
        newAsset.asset.function = elvis(values.function);
        newAsset.asset.previousSurvey = elvis(values.previousSurvey);
        newAsset.valid = isValid;

        props.parentCallback(newAsset);
    };

    const materialSelect = () => (
        <Select disabled={!isEditMode} allowClear>
            {materialTypes.map(name => (
                <Option key={name} value={name}>
                    {t('buildingEnums.material.' + name, name)}
                </Option>
            ))}
        </Select>
    );

    return (
        <Form
            form={form}
            {...formItemLayout}
            className="details_form"
            onValuesChange={handleFormChange}
            onFieldsChange={handleFormChange}
        >
            <Title level={4}>Added block</Title>
            <Form.Item label="Position" name="position">
                <Select disabled={!isEditMode} allowClear>
                    {BLOCK_POSITIONS.map(opt => (
                        <Option key={opt.value} value={opt.value}>{opt.label}</Option>
                    ))}
                </Select>
            </Form.Item>
            <Form.Item label="Vertical structure material" name="verticalMat">
                {materialSelect()}
            </Form.Item>
            <Form.Item label="Horizontal structure material" name="horizontalMat">
                {materialSelect()}
            </Form.Item>
            <Form.Item label="Number of floors" name="nFloors">
                <InputNumber disabled={!isEditMode} min={0} />
            </Form.Item>
            <Form.Item label="Total height of the block (m)" name="totalHeight">
                <InputNumber disabled={!isEditMode} min={0} step={0.1} />
            </Form.Item>
            <Form.Item label="Average floor surface (m2)" name="floorSurface">
                <InputNumber disabled={!isEditMode} min={0} step={0.1} />
            </Form.Item>
            <Form.Item label="Floor height (m)" name="floorHeight">
                <InputNumber disabled={!isEditMode} min={0} step={0.1} />
            </Form.Item>
            <Form.Item label="Function" name="function">
                <Select disabled={!isEditMode} allowClear>
                    {functionTypes.map(name => (
                        <Option key={name} value={name}>
                            {t('buildingEnums.addedBlockFunction.' + name, name)}
                        </Option>
                    ))}
                </Select>
            </Form.Item>
            <Form.Item label="ID of previous survey" name="previousSurvey">
                <Input disabled={!isEditMode} allowClear />
            </Form.Item>
        </Form>
    );
};

export default AddedBlockForm;
